import * as React from 'react';
import * as styles from "./DesignEditPanel.css";
import { inject, observer } from 'mobx-react';
import { LocalizationService } from '../../../services/LocalizationService';
import { AppAdminStore } from '../stores';
import { IAPanel } from '../../panels';
import { IAGroup } from '../../group/Group';
import { Environment } from '../../../services/Environment';
import { IAMessageBar } from '../../messageBar/MessageBar';
import { IAColorPicker } from '../../colorPicker/ColorPicker';
import { IAOptionSelector } from '../../optionSelector/OptionSelector';
import { IAMediaSelector } from '../../mediaSelector/MediaSelector';

export interface IEditPanelProps {
  appAdminStore?: AppAdminStore;
  open: boolean;
  previewSplashScreen: boolean;
  hasChanged?: () => void;
}


export interface IEditPanelState {
  hasCustomGifSplash?: boolean;
  hasCustomJpgSplash?: boolean;
  hasCustomIcon?: boolean;
}

@inject('appAdminStore')
@observer
export class DesignEditPanel extends React.Component<IEditPanelProps, IEditPanelState> {

  private localizationService: LocalizationService = new LocalizationService();

  constructor(props) {
    super(props);
    this.state = {
      hasCustomGifSplash: true,
      hasCustomJpgSplash: true,
      hasCustomIcon: true
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public render(): JSX.Element {
    return (
      <IAPanel
        showNavigationBar={true}
        component={"AppAdmin"}
        open={this.props.open}
        transition="slide"
        width={600}
        isMobile={false}
        highlightColor={"#23879b"}
        marginTop={location.origin.indexOf("sharepoint.com") !== -1 ? 159 : 50}
        disableSaving={false}
        overlayColor={"transparent"}
        close={() => {
          this.props.appAdminStore.editDesign = false;
        }}
      >
        {this.props.appAdminStore.instance.settings &&
          <div
            className={styles.IA_editPanel}
            id="IA_DesignEditPanel"
          >
            <IAGroup
              label={this.localizationService.strings.AppAdmin_NavigationBar}
            >
              {this.props.appAdminStore.editDesign &&
                <IAMediaSelector
                  label={this.localizationService.strings.AppAdmin_Logo}
                  showPreview={true}
                  highlightColor={"#23879b"}
                  token={this.props.appAdminStore.token}
                  tenant={this.props.appAdminStore.tenant}
                  selectedImage={this.props.appAdminStore.instance.settings.design.logo}
                  environment={this.props.appAdminStore.environment}
                  uploadUrl={`${Environment.getEnvironmentForMainAPI(this.props.appAdminStore.environment)}/api/${this.props.appAdminStore.tenant}.sharepoint.com/image?type=logo`}
                  fileType="image"
                  panelTop={159}
                  onChange={(logo: string) => {
                    this.props.appAdminStore.instance.settings.design.logo = logo;
                    this.props.appAdminStore.checkForChanges();

                    // We need to set the color to something different to trigger a rerender (thank you MOBX 🥲)
                    const oldColor = this.props.appAdminStore.instance.settings.design.navigationBarColor;
                    this.props.appAdminStore.instance.settings.design.navigationBarColor = "#000000";
                    this.props.appAdminStore.instance.settings.design.navigationBarColor = oldColor;
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                />
              }
              <div
                style={{
                  float: "left",
                  clear: "both",
                  marginTop: 15,
                  width: "100%"
                }}
              >
                <IAMessageBar
                  type="information"
                  onDismiss={() => { }}
                  icon="Info"
                  content={this.localizationService.strings.AppAdmin_LogoHelpText}
                  showIcon={true}
                  showCloseButton={false}
                  style={{ borderRadius: 5, width: this.props.appAdminStore.instance?.settings?.design?.logo ? "100%" : "calc(100% - 10px)" }}
                />
              </div>
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_NavigationBarColor}
                color={this.props.appAdminStore.instance.settings.design.navigationBarColor}
                onChange={(navigationBarColor: string) => {
                  this.props.appAdminStore.instance.settings.design.navigationBarColor = navigationBarColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }}
                showClearButton={false}
              />
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_NavigationBarContentColor}
                color={this.props.appAdminStore.instance.settings.design.navigationBarContentColor}
                onChange={(navigationBarContentColor: string) => {
                  this.props.appAdminStore.instance.settings.design.navigationBarContentColor = navigationBarContentColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }}
                showClearButton={false}
              />
            </IAGroup>
            <IAGroup
              label={this.localizationService.strings.AppAdmin_ContentArea}
            >
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_ContentAreaColor}
                color={this.props.appAdminStore.instance.settings.design.contentAreaColor}
                onChange={(contentAreaColor: string) => {
                  this.props.appAdminStore.instance.settings.design.contentAreaColor = contentAreaColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }}
                showClearButton={false}
                styles={{ marginTop: 0 }}
              />
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_ContentAreaContentColor}
                color={this.props.appAdminStore.instance.settings.design.contentAreaContentColor}
                onChange={(contentAreaContentColor: string) => {
                  this.props.appAdminStore.instance.settings.design.contentAreaContentColor = contentAreaContentColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }}
                showClearButton={false}
                styles={{ marginTop: 0 }}
              />
              <IAColorPicker
                label={this.localizationService.strings.AppAdmin_ContentAreaHighlightColor}
                color={this.props.appAdminStore.instance.settings.design.contentAreaHighlightColor}
                onChange={(contentAreaHighlightColor: string) => {
                  this.props.appAdminStore.instance.settings.design.contentAreaHighlightColor = contentAreaHighlightColor;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }}
                showClearButton={false}
                styles={{ marginTop: 0 }}
              />
            </IAGroup>
            <IAGroup
              label={this.localizationService.strings.AppAdmin_Menu}
            >
              <IAOptionSelector
                label={this.localizationService.strings.AppAdmin_MenuType}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Line%20Horizontal%203/SVG/ic_fluent_line_horizontal_3_20_regular.svg",
                    key: 'Burger',
                    text: this.localizationService.strings.AppAdmin_BurgerMenu,
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Web%20Asset/SVG/ic_fluent_web_asset_24_regular.svg",
                    key: 'TabBar',
                    text: new LocalizationService().strings.AppAdmin_TabBar,
                  },
                ]}
                selected={this.props.appAdminStore.instance.settings.design.menuStyle}
                highlightColor={"#23879b"}
                onChange={(menuStyle: "Burger" | "TabBar") => {
                  this.props.appAdminStore.instance.settings.design.menuStyle = menuStyle;
                  this.props.appAdminStore.checkForChanges();
                  if (this.props.hasChanged) {
                    this.props.hasChanged();
                  }
                }}
                style={{ clear: "none", marginTop: 0 }}
                borderRadius={5}
              />
              {this.props.appAdminStore.instance.settings.design.menuStyle === "TabBar" &&
                <div
                  style={{
                    float: "left",
                    clear: "both",
                    marginTop: 10,
                    width: "100%"
                  }}
                >
                  <IAMessageBar
                    type="information"
                    onDismiss={() => { }}
                    icon="Info"
                    content={this.localizationService.strings.AppAdmin_TabBarHelpText}
                    showIcon={true}
                    showCloseButton={false}
                    style={{ borderRadius: 5, width: "calc(100% - 10px)" }}
                  />
                </div>
              }
              {this.props.appAdminStore.instance.settings.design.menuStyle === "Burger" &&
                <IAColorPicker
                  label={this.localizationService.strings.AppAdmin_MenuColor}
                  color={this.props.appAdminStore.instance.settings.design.menuColor}
                  onChange={(menuColor: string) => {
                    this.props.appAdminStore.instance.settings.design.menuColor = menuColor;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                  showClearButton={false}
                  styles={{ clear: "both" }}
                />
              }
              {this.props.appAdminStore.instance.settings.design.menuStyle === "Burger" &&
                <IAColorPicker
                  label={this.localizationService.strings.AppAdmin_MenuContentColor}
                  color={this.props.appAdminStore.instance.settings.design.menuContentColor}
                  onChange={(menuContentColor: string) => {
                    this.props.appAdminStore.instance.settings.design.menuContentColor = menuContentColor;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                  showClearButton={false}
                />
              }
              {this.props.appAdminStore.instance.settings.design.menuStyle === "Burger" &&
                <IAColorPicker
                  label={this.localizationService.strings.AppAdmin_MenuHighlightColor}
                  color={this.props.appAdminStore.instance.settings.design.menuHighlightColor}
                  onChange={(menuHighlightColor: string) => {
                    this.props.appAdminStore.instance.settings.design.menuHighlightColor = menuHighlightColor;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                  showClearButton={false}
                />
              }
            </IAGroup>
            {this.props.appAdminStore.instance.settings.design.menuStyle === "TabBar" &&
              <IAGroup
                label={this.localizationService.strings.AppAdmin_TabBar}
              >
                <IAColorPicker
                  label={this.localizationService.strings.AppAdmin_TabBarColor}
                  color={this.props.appAdminStore.instance.settings.design.tabBarColor}
                  onChange={(tabBarColor: string) => {
                    this.props.appAdminStore.instance.settings.design.tabBarColor = tabBarColor;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                  showClearButton={false}
                  styles={{ marginTop: 0 }}
                />
                <IAColorPicker
                  label={this.localizationService.strings.AppAdmin_TabBarContentColor}
                  color={this.props.appAdminStore.instance.settings.design.tabBarContentColor}
                  onChange={(tabBarContentColor: string) => {
                    this.props.appAdminStore.instance.settings.design.tabBarContentColor = tabBarContentColor;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                  showClearButton={false}
                  styles={{ marginTop: 0 }}
                />
                <IAColorPicker
                  label={this.localizationService.strings.AppAdmin_TabBarHighlightColor}
                  color={this.props.appAdminStore.instance.settings.design.tabBarHighlightColor}
                  onChange={(tabBarHighlightColor: string) => {
                    this.props.appAdminStore.instance.settings.design.tabBarHighlightColor = tabBarHighlightColor;
                    this.props.appAdminStore.checkForChanges();
                    if (this.props.hasChanged) {
                      this.props.hasChanged();
                    }
                  }}
                  showClearButton={false}
                  styles={{ marginTop: 0 }}
                />
              </IAGroup>
            }
            
          </div>
        }
      </IAPanel>
    );
  }
}